import { useEffect, useRef, useState } from 'react'

import { useRuleActionNavigation } from '../../shared/hooks/useRuleActionNavigation'
import {
  FeatureFlag,
  useFeatureFlag,
  useFeatureFlags,
} from '@/hooks/useFeatureFlags'
import { useSelector } from 'react-redux'

import { Box } from '@chakra-ui/react'
import { AnimatePresence } from 'framer-motion'

import { AudioPlayer } from '@/components/ui'
import { IncidentQuery } from '@/graphql/generated/operations'
import { selectMe } from '@/redux/me/meSlice'
import { mixpanel } from '@/utils/analytics'

import { MixpanelDataIProps } from '../../shared/types/types'
import {
  getActivityTimeline,
  getTimelineFingerprint,
} from '../../shared/utils/feedUtils'
import { FeedItem } from './FeedItem'
import { FeedItemsSkeleton } from './FeedItemsSkeleton'
import { ActionHeader, ActionIcon } from './IncidentAction'
import { IncidentDetails } from './IncidentDetails'
import { IncidentNoteMedia } from './IncidentNoteMedia'
import { ResolutionContentItem } from './ResolutionContentItem'
import { ShowMore } from './ShowMore'

interface FeedIProps {
  incident?: IncidentQuery['incident']
  mixpanelData?: MixpanelDataIProps
  isLoading?: boolean
  onClose?: () => void
}

export const Feed = ({
  incident,
  mixpanelData,
  isLoading = false,
  onClose,
}: FeedIProps) => {
  const isTraceLogsEnabled = useFeatureFlag(FeatureFlag.traceLogs)
  const [items, setItems] = useState(null)
  const me = useSelector(selectMe)
  const {
    attachMediaToIncidentNote: isAttachMediaToIncidentNoteEnabled,
    salesDatabaseReset: isSalesEnvironment,
  } = useFeatureFlags()

  const handleRuleActionClick = useRuleActionNavigation({
    mixpanelData,
    onClose,
    items,
  })

  // Track previous fingerprint to avoid unnecessary rebuilds
  const prevFingerprintRef = useRef<string | null>(null)

  useEffect(() => {
    const fingerprint = getTimelineFingerprint(incident)

    // Skip rebuild if data hasn't actually changed
    if (fingerprint && fingerprint === prevFingerprintRef.current) {
      return
    }
    prevFingerprintRef.current = fingerprint

    const actions = incident?.actions
    const events = incident?.events
    const audioMessages = incident?.audioMessages?.edges?.map((a) => a?.node)
    const notes = incident?.notes.edges?.map((a) => a?.node)

    if (actions && audioMessages && notes && events) {
      const activityTimeLine = getActivityTimeline(
        actions,
        events,
        audioMessages,
        notes,
        me,
        isSalesEnvironment
      )
      setItems(activityTimeLine)
    }
  }, [incident, me, isSalesEnvironment])

  const trackExpandDescriptionClick = (eventType: 'more' | 'less'): void => {
    let eventName: string
    switch (eventType) {
      case 'more':
        eventName = 'Show More Button Pressed (Feed)'
        break
      case 'less':
        eventName = 'Show Less Button Pressed (Feed)'
        break
    }
    mixpanel.track(eventName, mixpanelData)
  }

  return (
    <Box align='flex-start' p='24px'>
      {isLoading || !items ? (
        <FeedItemsSkeleton />
      ) : (
        <AnimatePresence>
          <IncidentDetails incident={incident} mixpanelData={mixpanelData} />

          {items?.length > 0 && (
            <Box
              as='p'
              color='black'
              fontSize='16px'
              fontWeight={600}
              mb='10px'
            >
              Timeline Feed
            </Box>
          )}

          {items.map((a, i: number) => (
            <FeedItem
              actionType={a?.type || a?.nonActionType}
              avatar={a?.creator?.avatar?.url}
              avatarName={`${a?.creator?.firstName ?? ''} ${
                a?.creator?.lastName ?? ''
              }`.trim()}
              createdAt={a?.createdAt}
              heading={ActionHeader({
                actionType: a?.type || a?.nonActionType,
                username: `${a?.creator?.firstName} ${a?.creator?.lastName}`,
                roleName: a?.creator?.roleName,
                systemMessage: a?.systemMessage,
                technicianName: `${a?.deviceRepairOrder?.technician?.firstName} ${a?.deviceRepairOrder?.technician?.lastName}`,
                history: a?.history,
                isRuleAction: a?.isRuleAction,
                emailRecipientCount: a?.emailRecipients?.length,
                onRuleClick: a?.ruleId
                  ? () => handleRuleActionClick(a.ruleId)
                  : undefined,
              })}
              icon={ActionIcon(a?.type || a?.nonActionType)}
              index={i}
              key={a?.id ?? i}
            >
              {a?.audioClip?.url && <AudioPlayer url={a?.audioClip?.url} />}
              {/* Resolution actions use resolutionContent.segments (supports TEXT and EVENT_REF types).
                  Notes use message field and render via ShowMore for plain text display. */}
              {a?.resolutionContent && isTraceLogsEnabled ? (
                <ResolutionContentItem
                  content={a.resolutionContent}
                  handleShowLess={() => trackExpandDescriptionClick('less')}
                  handleShowMore={() => trackExpandDescriptionClick('more')}
                />
              ) : a?.message ? (
                <ShowMore
                  handleShowLess={() => trackExpandDescriptionClick('less')}
                  handleShowMore={() => trackExpandDescriptionClick('more')}
                  message={a.message}
                />
              ) : null}
              {isAttachMediaToIncidentNoteEnabled && a?.media?.length > 0 && (
                <IncidentNoteMedia files={a?.media} />
              )}
            </FeedItem>
          ))}
        </AnimatePresence>
      )}
    </Box>
  )
}
